import { asError } from '#/lib/asError';
import getLogger from '#/services/loggerFactory';
import { createSelfServiceApi } from '#/services/rentalSelfServiceApiClient';
import type { TokenResponse } from '#/typings/TokenResponse';
import type { UserProfile as User, UserContext } from '@auth0/nextjs-auth0/client';
import { datadogRum } from '@datadog/browser-rum';
import clientConfig from '@jucy-nakuru/config/clientConfig';
import { sub } from 'date-fns';
import { setUserId } from 'firebase/analytics';
import type { User as FirebaseUser, UserCredential } from 'firebase/auth';
import { onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import jwt_decode, { JwtPayload } from 'jwt-decode';
import { autorun, makeAutoObservable, reaction, runInAction } from 'mobx';
import ErrorReporter from '../lib/ErrorReporter';
import { analytics, auth as firebaseAuth } from '../services/firebaseClient';
import UserProfile from './models/UserProfile';

const logger = getLogger(import.meta.url);

const getTokens = async (refresh?: boolean) => {
    const response = await fetch(`/api/auth/tokens${refresh ? '?refresh=true' : ''}`);
    return (await response.json()) as TokenResponse;
};
const getProfile = async (refresh?: boolean) => {
    const response = await fetch(`/api/auth/me${refresh ? '?refresh=true' : ''}`);
    return (await response.json()) as UserProfile & { error?: string };
};

export default class AuthStore {
    auth0?: UserContext;
    profile?: UserProfile;
    profileLoading = false;
    profileRefreshing = false;
    error?: Error;
    user?: User;
    idToken?: string;
    dbToken?: string;
    accessToken?: string;
    dbCredentials?: UserCredential;
    dbTokenLoading = false;
    dbLoading = true;
    dbUser?: FirebaseUser;
    permissions: string[] = [];

    constructor(initialData?: Partial<AuthStore>) {
        makeAutoObservable(this);
        Object.assign(this, initialData);
        const sessionUser = typeof window !== 'undefined' && window.sessionStorage.getItem('user');
        if (sessionUser) {
            this.profile = new UserProfile(JSON.parse(sessionUser) as Partial<UserProfile>);
        }
        this.initTokens();
        this.initProfile();
        this.initFirebaseAuth();

        reaction(
            () => ({
                isLoading: this.isLoading,
                isAuthenticated: this.isAuthenticated,
            }),
            ({ isLoading, isAuthenticated }) => {
                if (!isLoading && !isAuthenticated) {
                    window.localStorage.clear();
                }
            }
        );

        autorun(() => {
            if (this.isAuthenticated && this.profile?.userId && typeof window !== 'undefined') {
                if (clientConfig?.hotjar?.id && clientConfig?.hotjar?.sv) {
                    import('react-hotjar').then(({ hotjar }) => {
                        if (this.profile?.userId) {
                            hotjar.identify('USER_ID', {
                                user_id: this.profile.userId,
                                signed_up: this.profile.created,
                                created: this.profile.created,
                                email: this.profile.email,
                            });
                        }
                    });
                }

                datadogRum.setUser({
                    id: this.profile.userId,
                    email: this.profile.email,
                    name: this.profile.name || '',
                });
            }
        });
    }

    get jwt() {
        try {
            return this.idToken ? jwt_decode<JwtPayload>(this.idToken) : undefined;
        } catch (e) {
            const error = asError(e);
            logger.captureException(asError(error), `Failed to decode jwt token - ${error.message}`);
            return undefined;
        }
    }

    renewIfExpired = async () => {
        if (this.jwt?.exp && !this.profileRefreshing) {
            const expires = new Date(this.jwt.exp * 1000);
            const expiresMinus5 = sub(expires, { minutes: 5 });
            const diff = Math.abs(new Date().getTime() - expiresMinus5.getTime()) / 60000;
            if (diff <= 5) {
                return this.refreshProfile();
            }
        }
    };

    get isLoading(): boolean {
        return Boolean(
            typeof this.auth0?.isLoading === 'undefined' || this.auth0.isLoading || this.dbTokenLoading || this.dbLoading || this.profileLoading
        );
    }

    get isAuthenticated(): boolean {
        return Boolean(this.profile && this.dbUser);
    }

    async refreshProfile(): Promise<UserProfile | null> {
        if (this.profileRefreshing) {
            return null;
        }
        runInAction(() => (this.profileRefreshing = true));
        if (this.isAuthenticated && !this.error && this.auth0) {
            try {
                const [profile, tokens] = await Promise.all([getProfile(true), getTokens(true)]);
                if (profile.error) {
                    throw new Error(profile.error);
                }
                runInAction(() => {
                    this.idToken = tokens?.idToken || undefined;
                    this.dbToken = tokens?.dbToken || undefined;
                    this.accessToken = tokens?.accessToken || undefined;
                    this.profile = profile || undefined;
                });
            } catch (error) {
                ErrorReporter.reportError({
                    error,
                    tags: {
                        location: 'AuthStore',
                    },
                });
                runInAction(() => (this.error = error));
            }
        }
        runInAction(() => (this.profileRefreshing = false));
        return this.profile || null;
    }

    hydrate = (data: Partial<AuthStore>): void => {
        Object.assign(this, data);
    };

    private initTokens = () =>
        autorun(() => {
            if (this.profile && this.profile?.userId && !this.idToken && !this.dbTokenLoading && !this.error) {
                runInAction(() => {
                    this.dbLoading = true;
                    this.dbTokenLoading = true;
                });
                getTokens()
                    .then((tokens) => {
                        if (tokens?.dbToken && tokens?.idToken && tokens?.accessToken) {
                            if (analytics && this.profile && this.profile.userId) {
                                setUserId(analytics, this.profile.userId);
                            }
                            runInAction(() => {
                                this.idToken = tokens.idToken || undefined;
                                this.dbToken = tokens.dbToken || undefined;
                                this.accessToken = tokens.accessToken || undefined;
                                this.dbTokenLoading = false;
                            });
                        }
                        if (firebaseAuth?.currentUser) {
                            runInAction(() => {
                                this.dbUser = firebaseAuth?.currentUser || undefined;
                                this.dbLoading = false;
                            });
                        } else if (firebaseAuth && tokens.dbToken) {
                            return signInWithCustomToken(firebaseAuth, tokens.dbToken);
                        }
                        return null;
                    })
                    .catch((e) => {
                        const error = asError(e);
                        ErrorReporter.reportError({
                            error,
                            tags: {
                                location: 'AuthStore',
                            },
                        });
                        runInAction(() => {
                            this.error = error;
                            this.idToken = undefined;
                            this.dbToken = undefined;
                            this.accessToken = undefined;
                            this.dbTokenLoading = false;
                            this.dbTokenLoading = false;
                        });
                    });
            }
        });

    private initProfile = () =>
        autorun(() => {
            if (this.user && !this.profile && !this.profileLoading && !this.error) {
                runInAction(() => {
                    this.profileLoading = true;
                });
                getProfile()
                    .then((profile) => {
                        if (profile.error) {
                            throw new Error(profile.error);
                        }
                        runInAction(() => (this.profile = new UserProfile(profile)));
                        if (analytics) {
                            setUserId(analytics, profile.userId);
                        }
                    })
                    .catch((e) => {
                        runInAction(() => {
                            this.profile = undefined;
                            this.error = asError(e);
                        });
                    })
                    .finally(() => {
                        runInAction(() => {
                            this.profileLoading = false;
                        });
                    });
            }
        });

    private initFirebaseAuth = () => {
        if (firebaseAuth) {
            onAuthStateChanged(firebaseAuth, (user) => {
                runInAction(() => {
                    this.dbUser = user || undefined;
                    this.dbLoading = false;
                });
            });
            autorun(() => {
                if (firebaseAuth && !firebaseAuth?.currentUser && this.dbToken && !this.dbTokenLoading) {
                    runInAction(() => (this.dbTokenLoading = true));
                    signInWithCustomToken(firebaseAuth, this.dbToken)
                        .catch((error) => {
                            ErrorReporter.reportError({
                                error: asError(error),
                                tags: {
                                    location: 'AuthStore',
                                },
                            });
                        })
                        .finally(() => {
                            runInAction(() => (this.dbTokenLoading = false));
                        });
                }
            });
        }
    };

    get selfServiceApiClient() {
        return this.accessToken ? createSelfServiceApi(this.accessToken) : undefined;
    }
}
